import React from "react";
import { Award } from "lucide-react";
import { SANDRA_PORTRAIT, SANDRA_PORTRAIT_2 } from "../mock";

const CERTS = [
  { main: "Certified Personal Trainer (NASM)" },
  { main: "Certified Exercise Physiologist (ACSM)" },
];

// The remaining certifications, with parentheses and their contents removed.
const OTHER_CERTS = [
  "Certified Online Personal Trainer",
  "Certified Nutritional Coach",
  "Certified Peptide Integration Specialist",
  "Certified Hormone Integration Specialist",
];

const MeetSandra = () => {
  return (
    <section id="about" className="py-12 md:py-20 bg-black">
      <div className="w-[90%] max-w-[1200px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Portrait — full image on black (both arms visible, no visible box) */}
          <div className="lg:col-span-5 order-1">
            <div className="relative space-y-6">
              <div className="absolute inset-0 -z-10 bg-primary/10 blur-[100px] rounded-full" />
              <div className="relative">
                <img
                  src={SANDRA_PORTRAIT}
                  alt="Sandra Guy, CEO & Founder of Sassy Full Stack Fitness, on the NPC bikini competition stage at age 56"
                  className="w-full h-auto object-contain"
                />
                {/* Cover the baked-in AI sparkle icon (feathered so no hard edge shows) */}
                <div
                  className="absolute bottom-0 right-0 w-[36%] h-[32%] pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse at 80% 78%, #000 0%, #000 48%, rgba(0,0,0,0) 78%)",
                  }}
                />
              </div>
              <img
                src={SANDRA_PORTRAIT_2}
                alt="Sandra Guy in professional attire — CEO & Founder"
                className="w-full h-auto object-contain"
                loading="lazy"
              />
            </div>
          </div>

          {/* Copy */}
          <div className="lg:col-span-7 order-2">
            <span className="text-[11px] font-bold tracking-[0.35em] uppercase text-primary">
              Meet Sandra
            </span>
            <h2 className="mt-3 font-display font-black uppercase tracking-tighter leading-[0.9] text-4xl md:text-5xl">
              Sandra <span className="text-gold-gradient">Guy</span>
            </h2>
            <p className="mt-2 text-sm font-bold tracking-[0.2em] uppercase text-white/60">
              CEO &amp; Founder
              <span className="hidden md:inline"> · Health &amp; Fitness Coach</span>
              <span className="block md:hidden">Health &amp; Fitness Coach</span>
            </p>

            <div className="mt-7 space-y-4 text-lg text-white/80 leading-relaxed font-medium">
              <p>
                Sandra Guy is a seasoned health and fitness professional. Sandra has been in the
                Health and Fitness industry for over 30 years and has literally helped thousands of
                people transform their health and fitness.
              </p>
              <p>
                Sandra graduated in 1992 from Troy University with a Bachelor of Science in Health
                Administration. Since then, Sandra has worked in the corporate health and fitness
                world with recognizable names like USAA, Xerox, Gold's Gym, and the St. Joe Company.
                Sandra has also owned her own gyms and online health coaching companies.
              </p>
              <p>
                Sandra is the founder of Sassy Full Stack Fitness which she started in March of
                2020.
              </p>
            </div>

            <div className="mt-9 space-y-7">
              <div>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl text-white">
                  Marathon · Triathlon · CrossFit
                </h3>
                <div className="mt-3 space-y-3 text-white/75 leading-relaxed font-medium">
                  <p>
                    A seasoned marathon runner, she competed in 8 marathons and claimed victory at
                    the 2005 Cherry Blossom Marathon in Macon, Georgia. She expanded into
                    triathlons, earning an age group win at the 2010 Diamond Lake Sprint in Florida.
                  </p>
                  <p>
                    Sandra dominated CrossFit competition by topping the master's division at the
                    Southeast Region CrossFit Open Championships four consecutive years
                    (2014–2017), while ranking among the top 200 CrossFit athletes worldwide in the
                    Master's Division.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="font-display font-bold uppercase tracking-tight text-xl text-white">
                  Menopause to Bikini
                </h3>
                <div className="mt-3 space-y-3 text-white/75 leading-relaxed font-medium">
                  <p>
                    At the early onset of her own Menopause journey, Sandra hired her own online
                    health and fitness coach to meet this part of life head on. Already what most
                    would consider healthy and in shape, but after a year her body changed so much
                    that she decided to trade in her feet, the bike and the barbell for the stage.
                  </p>
                  <p>
                    In her very first National Physique Committee (NPC) bikini contest Sandra won
                    the Dexter Jackson Classic event in 2019 in the 50 and over age division. Sandra
                    continues to compete in regional and national NPC events, now in the Women's
                    Bikini 55+ division.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-9">
              <h3 className="font-display font-bold uppercase tracking-tight text-xl text-white mb-4">
                Certifications
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {CERTS.map((c) => (
                  <li
                    key={c.main}
                    className="flex items-start gap-2.5 text-white/80 font-medium text-[15px]"
                  >
                    <Award size={18} className="text-primary shrink-0 mt-0.5" />
                    <span className={c.nowrap ? "whitespace-nowrap" : ""}>
                      {c.main}
                      {c.sub && <span className="block">{c.sub}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-9" data-testid="other-certifications">
              <h3 className="font-display font-bold uppercase tracking-tight text-xl text-white mb-4">
                Other Certifications
              </h3>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {OTHER_CERTS.map((name) => (
                  <li
                    key={name}
                    className="flex items-start gap-2.5 text-white/80 font-medium text-[15px]"
                  >
                    <Award size={18} className="text-primary shrink-0 mt-0.5" />
                    <span>{name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MeetSandra;
